import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getDaysInMonth, isDateSelectable } from './scheduleHelpers';
import { fadeInRight, transitionMedium } from '../../utils/animations';

// Get locale string from language code
const getLocale = (lang) => {
  const locales = { ko: 'ko-KR', ja: 'ja-JP', en: 'en-US' };
  return locales[lang] || locales.en;
};

// Helper function to determine date button styling
const getDateButtonClasses = (date, selectedDate) => {
  const baseClasses = 'aspect-square flex items-center justify-center rounded-lg text-sm';
  
  if (!date) {
    return baseClasses;
  }
  
  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
  const isSelectable = isDateSelectable(date);
  
  if (isSelected) {
    return `${baseClasses} bg-slate-900 text-white hover:bg-slate-800`;
  }
  
  if (isSelectable) {
    return `${baseClasses} hover:bg-slate-100 cursor-pointer`;
  }
  
  return `${baseClasses} text-slate-300 cursor-not-allowed`;
};

/**
 * Date Selection Calendar Component
 */
const DateSelect = ({ 
  t, 
  language,
  currentMonth, 
  setCurrentMonth,
  selectedDate,
  onDateSelect, 
  onBack 
}) => {
  const locale = getLocale(language);
  
  return (
    <motion.div
      initial={fadeInRight.initial}
      animate={fadeInRight.animate}
      transition={transitionMedium}
      className="bg-white rounded-2xl shadow-sm p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-900">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-slate-900">{t.selectDate}</h2>
        <div className="w-5" />
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="font-medium">
          {currentMonth.toLocaleDateString(locale, {
            month: 'long',
            year: 'numeric'
          })}
        </span>
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {getDaysInMonth(currentMonth).map((date, index) => (
          <button
            key={date ? date.toISOString() : `empty-${index}`}
            onClick={() => date && isDateSelectable(date) && onDateSelect(date)}
            disabled={!date || !isDateSelectable(date)}
            className={getDateButtonClasses(date, selectedDate)}
          >
            {date?.getDate()}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

export default DateSelect;
